import React, { useMemo } from 'react';

import { useGetUsersCount } from '@vlabs/api/artemy/client';
import type { GetUsersCountParams, GetUsersParams } from '@vlabs/api/artemy/model';
import {
  UserListFilterMenuButton,
  UserListFastFilters,
  UserListSortDropdown,
  useUserListFilters,
} from '@vlabs/monokit/entities/users/filters';
import { CreateUserButton } from '@vlabs/monokit/features/point/create-user';
import { SearchUsersBy } from '@vlabs/monokit/features/point/search-users-by';
import { PageHeader, PageSubheader, usePageContext } from '@vlabs/monokit/shared/ui/page';
import { withPermissions } from '@vlabs/permissions';

import st from './UserListPageHeader.module.sass';
import { useUserListPageTranslation } from '../i18n';

const CreateUserWithAccess = withPermissions('users', 'create')(CreateUserButton);

function getUsersCountParams(filters: GetUsersParams): GetUsersCountParams {
  const {
    page,
    page_size: pageSize,
    sort,
    order,
    ...usersCountParams
  } = filters;

  return usersCountParams;
}

function UserListPageHeader(): React.JSX.Element {
  const { t } = useUserListPageTranslation();
  const pageCtx = usePageContext();
  const { filters } = useUserListFilters();
  const usersCountParams = useMemo(() => getUsersCountParams(filters), [filters]);
  const getUsersCount = useGetUsersCount(usersCountParams);
  const usersCount = getUsersCount.data?.users_count ?? 0;
  const $title = useMemo(() => (
    <div className={st.Title}>
      {pageCtx.getTitleComponent(t('title'))}
      <SearchUsersBy
        className={st.SearchUsersBy}
        size={pageCtx.inputSize}
      />
    </div>
  ), [t, pageCtx]);

  return (
    <React.Fragment>
      <PageHeader
        actions={(
          <React.Fragment>
            <CreateUserWithAccess size={pageCtx.buttonSize} />
            <UserListFilterMenuButton />
          </React.Fragment>
        )}
        title={$title}
      />
      <PageSubheader
        className={st.Subheader}
        title={(
          <div className={st.Count}>
            {t('users_found', { count: usersCount })}
          </div>
        )}
      >
        <div className={st.SubheaderContent}>
          <div className={st.SubheaderFastFilters}>
            <UserListFastFilters />
          </div>
          <div className={st.SubheaderActions}>
            <UserListSortDropdown />
          </div>
        </div>
      </PageSubheader>
    </React.Fragment>
  );
}

export { UserListPageHeader };
